import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X, MapPin, Users, Search, Star, Globe2 } from "lucide-react";

/**
 * AudienceFilterPicker
 *
 * Chip-multiselect with type-ahead for narrowing a promotion blast.
 * The picker walks through a hierarchical scope selector:
 *   Nationwide  → no geo filters (default; every opted-in customer)
 *   By state    → state chips only
 *   By city     → state + city chips; city suggestions restricted to picked states
 *   By ZIP code → state + city + ZIP chips; ZIP suggestions restricted to picked cities/states
 *
 * The star-rating audience filter is available for every scope.
 *
 * Props:
 *   value: { zips: string[], cities: string[], states: string[], min_rating: number|null }
 *   onChange: (next) => void
 */
export default function AudienceFilterPicker({ value, onChange }) {
  const v = value || { zips: [], cities: [], states: [], min_rating: null };
  const [audience, setAudience] = useState({ zips: [], cities: [], states: [], rating_buckets: null, total_opted_in: 0 });
  const [loaded, setLoaded] = useState(false);

  // Infer the initial scope from any existing selections so an edit-in-place feels natural.
  const inferScope = () => {
    if ((v.zips || []).length) return "zip";
    if ((v.cities || []).length) return "city";
    if ((v.states || []).length) return "state";
    return "nationwide";
  };
  const [scope, setScope] = useState(inferScope());

  useEffect(() => {
    api.get("/me/business/customer-audience")
      .then(({ data }) => setAudience(data))
      .catch((e) => console.warn("audience load failed:", e))
      .finally(() => setLoaded(true));
  }, []);

  const changeScope = (next) => {
    setScope(next);
    // Reset every filter that becomes irrelevant when the scope narrows/widens.
    if (next === "nationwide") {
      onChange({ ...v, zips: [], cities: [], states: [] });
    } else if (next === "state") {
      onChange({ ...v, zips: [], cities: [] });
    } else if (next === "city") {
      onChange({ ...v, zips: [] });
    }
  };

  const hasAnyFilter =
    (v.zips?.length || 0) + (v.cities?.length || 0) + (v.states?.length || 0) > 0 ||
    (v.min_rating != null && v.min_rating > 0);

  const setRating = (n) => onChange({ ...v, min_rating: n });
  const rb = audience.rating_buckets || {};
  const ratedCount = (rb["1_to_2"] || 0) + (rb["2_to_3"] || 0) + (rb["3_to_4"] || 0) + (rb["4_to_5"] || 0) + (rb["5"] || 0);
  const ratingOptions = [
    { v: null, label: "Any", hint: `${audience.total_opted_in || 0} opted-in` },
    { v: 2, label: "≥ 2★", hint: `${(rb["2_to_3"] || 0) + (rb["3_to_4"] || 0) + (rb["4_to_5"] || 0) + (rb["5"] || 0)} match` },
    { v: 3, label: "≥ 3★", hint: `${(rb["3_to_4"] || 0) + (rb["4_to_5"] || 0) + (rb["5"] || 0)} match` },
    { v: 4, label: "≥ 4★", hint: `${(rb["4_to_5"] || 0) + (rb["5"] || 0)} match` },
    { v: 5, label: "5★ only", hint: `${rb["5"] || 0} match` },
  ];

  // Cascade filters for suggestions:
  //   • City suggestions constrained to selected states (if any)
  //   • ZIP suggestions constrained to selected cities and/or states (if any)
  const norm = (s) => (s || "").toString().trim().toLowerCase();
  const selectedStatesNorm = new Set((v.states || []).map(norm));
  const selectedCitiesNorm = new Set((v.cities || []).map(norm));

  const filteredCitySuggestions = useMemo(() => {
    const list = audience.cities || [];
    if (!selectedStatesNorm.size) return list;
    return list.filter((c) => selectedStatesNorm.has(norm(c.state_norm || c.state)));
  }, [audience.cities, v.states, selectedStatesNorm]);

  const filteredZipSuggestions = useMemo(() => {
    const list = audience.zips || [];
    return list.filter((z) => {
      if (selectedCitiesNorm.size && !selectedCitiesNorm.has(norm(z.city_norm || z.city))) return false;
      if (selectedStatesNorm.size && !selectedStatesNorm.has(norm(z.state_norm || z.state))) return false;
      return true;
    });
  }, [audience.zips, v.cities, v.states, selectedCitiesNorm, selectedStatesNorm]);

  const scopeChips = [
    { key: "nationwide", label: "Nationwide", icon: Globe2, hint: "Every opted-in customer" },
    { key: "state", label: "By state", icon: MapPin, hint: "Pick one or more states" },
    { key: "city", label: "By city", icon: MapPin, hint: "Drill into cities (optionally within states)" },
    { key: "zip", label: "By ZIP code", icon: MapPin, hint: "Narrow further to specific ZIPs" },
  ];

  return (
    <div className="border border-border rounded-md bg-white p-4 space-y-4" data-testid="audience-filter-picker">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <MapPin size={16} className="text-[#B31E1E]" />
            <h4 className="font-display text-lg">Where should this go?</h4>
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono">Optional</Badge>
          </div>
          <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
            Choose a scope and drill down. Nationwide reaches every opted-in customer ({audience.total_opted_in || 0}); anything narrower filters by ZIP / city / state — and by star rating.
          </p>
        </div>
        {hasAnyFilter && (
          <button
            type="button"
            onClick={() => {
              setScope("nationwide");
              onChange({ zips: [], cities: [], states: [], min_rating: null });
            }}
            className="text-xs font-mono text-gray-500 hover:text-[#B31E1E] hover:underline"
            data-testid="audience-clear-all"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Scope drill-down selector */}
      <div className="flex flex-wrap gap-1.5" data-testid="audience-scope-row" role="tablist">
        {scopeChips.map((s) => {
          const Icon = s.icon;
          const active = scope === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => changeScope(s.key)}
              className={`px-3 py-1.5 rounded-[35px] text-xs font-mono border inline-flex items-center gap-1.5 transition-colors ${
                active
                  ? "bg-[#B31E1E] text-white border-[#B31E1E]"
                  : "bg-white text-gray-700 border-border hover:bg-gray-50"
              }`}
              data-testid={`audience-scope-${s.key}`}
              title={s.hint}
              role="tab"
              aria-selected={active}
            >
              <Icon size={12} />
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Star-rating filter — always visible */}
      <div data-testid="audience-rating-row">
        <div className="flex items-center gap-2">
          <Star size={13} className="text-amber-500" />
          <Label className="text-xs font-mono uppercase tracking-wider text-gray-600">
            Customer star rating
          </Label>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {ratingOptions.map((opt) => {
            const active = (v.min_rating || null) === opt.v;
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => setRating(opt.v)}
                className={`px-3 py-1.5 rounded-[35px] text-xs font-mono border transition-colors ${
                  active
                    ? "bg-[#B31E1E] text-white border-[#B31E1E]"
                    : "bg-white text-gray-700 border-border hover:bg-gray-50"
                }`}
                data-testid={`audience-rating-${opt.v == null ? "any" : opt.v}`}
                title={opt.hint}
              >
                {opt.label}
                <span className={`ml-1.5 text-[10px] ${active ? "text-white/70" : "text-gray-400"}`}>
                  {opt.hint}
                </span>
              </button>
            );
          })}
        </div>
        {v.min_rating != null && v.min_rating > 0 && (
          <p className="text-[11px] text-gray-500 mt-1.5" data-testid="audience-rating-hint">
            Customers with no reviews yet ({rb.unrated || 0}) are <strong>excluded</strong>.
            Out of {audience.total_opted_in || 0} opted-in, {ratedCount} have at least one review.
          </p>
        )}
      </div>

      {/* Nationwide short-circuit — no geo fields to render */}
      {scope === "nationwide" && (
        <div className="rounded-md border border-dashed border-border bg-gray-50 px-3 py-3 text-xs text-gray-600 inline-flex items-center gap-2" data-testid="audience-scope-nationwide-hint">
          <Globe2 size={13} className="text-[#B31E1E]" />
          Sending nationwide — no state, city, or ZIP filter applied.
        </div>
      )}

      {/* State field — visible for state/city/zip scopes */}
      {(scope === "state" || scope === "city" || scope === "zip") && (
        <FilterChipField
          label="States"
          testIdPrefix="audience-state"
          placeholder="Type a state and press Enter (e.g. CA)"
          sanitize={(s) => s.trim().slice(0, 40)}
          validate={(s) => s.length >= 2}
          items={v.states || []}
          suggestions={audience.states || []}
          loaded={loaded}
          onChange={(arr) => {
            // Removing a state should also drop cities/zips that only made sense within it.
            const nextStates = arr;
            const keepStateNorm = new Set(nextStates.map(norm));
            const nextCities = (v.cities || []).filter((c) => {
              // We don't have a state hint on already-picked city chips (user typed them);
              // keep them if the aggregate audience data suggests they live in a still-selected state.
              const s = (audience.cities || []).find((x) => norm(x.value) === norm(c));
              if (!s) return true;
              return keepStateNorm.size === 0 || keepStateNorm.has(norm(s.state_norm || s.state));
            });
            const nextZips = (v.zips || []).filter((z) => {
              const s = (audience.zips || []).find((x) => x.value === z);
              if (!s) return true;
              return keepStateNorm.size === 0 || keepStateNorm.has(norm(s.state_norm || s.state));
            });
            onChange({ ...v, states: nextStates, cities: nextCities, zips: nextZips });
          }}
        />
      )}

      {/* City field — visible for city/zip scopes */}
      {(scope === "city" || scope === "zip") && (
        <FilterChipField
          label="Cities"
          testIdPrefix="audience-city"
          placeholder={selectedStatesNorm.size ? "Cities in your picked states…" : "Type a city name and press Enter (e.g. Berkeley)"}
          sanitize={(s) => s.trim().slice(0, 80)}
          validate={(s) => s.length >= 2}
          items={v.cities || []}
          suggestions={filteredCitySuggestions}
          loaded={loaded}
          onChange={(arr) => {
            const nextCities = arr;
            const keepCityNorm = new Set(nextCities.map(norm));
            const nextZips = (v.zips || []).filter((z) => {
              const s = (audience.zips || []).find((x) => x.value === z);
              if (!s) return true;
              return keepCityNorm.size === 0 || keepCityNorm.has(norm(s.city_norm || s.city));
            });
            onChange({ ...v, cities: nextCities, zips: nextZips });
          }}
        />
      )}

      {/* ZIP field — visible only for zip scope */}
      {scope === "zip" && (
        <FilterChipField
          label="ZIP codes"
          testIdPrefix="audience-zip"
          placeholder={selectedCitiesNorm.size ? "ZIPs in your picked cities…" : "Type a ZIP and press Enter (e.g. 94704)"}
          sanitize={(s) => s.replace(/\D/g, "").slice(0, 10)}
          validate={(s) => s.length >= 5}
          items={v.zips || []}
          suggestions={filteredZipSuggestions}
          loaded={loaded}
          onChange={(arr) => onChange({ ...v, zips: arr })}
        />
      )}

      <p className="text-[11px] font-mono text-gray-400 inline-flex items-center gap-1.5" data-testid="audience-summary">
        <Users size={11} />
        {scope === "nationwide"
          ? `Nationwide → all ${audience.total_opted_in || 0} opted-in customers`
          : `Scope: ${scope} · ${(v.states || []).length} state(s), ${(v.cities || []).length} city(ies), ${(v.zips || []).length} ZIP(s)`}
      </p>
    </div>
  );
}

function FilterChipField({ label, testIdPrefix, placeholder, sanitize, validate, items, suggestions, loaded, onChange }) {
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);

  const filteredSuggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    const taken = new Set(items.map((i) => String(i).toLowerCase()));
    return (suggestions || [])
      .filter((s) => {
        const val = String(s.value || s).toLowerCase();
        if (taken.has(val)) return false;
        if (!q) return true;
        return val.includes(q);
      })
      .slice(0, 6);
  }, [draft, suggestions, items]);

  const add = (raw) => {
    const cleaned = sanitize(raw);
    if (!validate(cleaned)) return;
    if (items.map((i) => String(i).toLowerCase()).includes(cleaned.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...items, cleaned]);
    setDraft("");
  };

  const removeAt = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (draft) add(draft);
    } else if (e.key === "Backspace" && !draft && items.length) {
      removeAt(items.length - 1);
    }
  };

  return (
    <div>
      <Label className="text-xs font-mono uppercase tracking-wider text-gray-600">{label}</Label>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-[#B31E1E] focus-within:ring-offset-1">
        {items.map((it, idx) => (
          <Badge
            key={`${it}-${idx}`}
            variant="outline"
            className="rounded-full pl-2.5 pr-1 py-0.5 text-xs font-mono inline-flex items-center gap-1 border-[#B31E1E]/40 bg-[#B31E1E]/5 text-[#B31E1E]"
            data-testid={`${testIdPrefix}-chip-${idx}`}
          >
            {it}
            <button
              type="button"
              onClick={() => removeAt(idx)}
              className="ml-0.5 rounded-full hover:bg-[#B31E1E]/15 p-0.5"
              aria-label={`Remove ${it}`}
              data-testid={`${testIdPrefix}-chip-remove-${idx}`}
            >
              <X size={11} />
            </button>
          </Badge>
        ))}
        <div className="relative flex-1 min-w-[180px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-300" />
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            placeholder={items.length === 0 ? placeholder : ""}
            className="h-8 border-0 shadow-none focus-visible:ring-0 pl-6 text-sm bg-transparent"
            data-testid={`${testIdPrefix}-input`}
          />
          {focused && loaded && filteredSuggestions.length > 0 && (
            <ul
              className="absolute left-0 right-0 mt-1 z-20 bg-white border border-border rounded-md shadow-md py-1 max-h-56 overflow-auto"
              data-testid={`${testIdPrefix}-suggestions`}
            >
              {filteredSuggestions.map((s, idx) => {
                const val = String(s.value || s);
                const count = s.count;
                return (
                  <li key={`${val}-${idx}`}>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); add(val); }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center justify-between gap-2"
                      data-testid={`${testIdPrefix}-suggestion-${idx}`}
                    >
                      <span className="font-mono">{val}</span>
                      {typeof count === "number" && (
                        <span className="text-[10px] font-mono text-gray-400">{count} customer{count === 1 ? "" : "s"}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      {draft && !validate(sanitize(draft)) && (
        <p className="text-[11px] text-rose-600 mt-1" data-testid={`${testIdPrefix}-error`}>
          {label === "ZIP codes" ? "Enter at least 5 digits" : "Too short"}
        </p>
      )}
    </div>
  );
}
